import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Sparkles, CheckCircle2, Loader2, ArrowRight, Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import ScrollReveal from "@/components/ScrollReveal";
import { track } from "@/lib/analytics";
import { saveParentLead } from "@/lib/parentLead";

const GRADES = ["Grade 8", "Grade 9", "Grade 10", "Grade 11", "Grade 12"];
const INTERESTS = ["WCC360", "School Partnership", "Parent Workshop", "Corporate Partnership", "Other"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[0-9\s\-()]{7,20}$/;

function readUtm(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((k) => {
    const v = p.get(k);
    if (v) out[k] = v.slice(0, 120);
  });
  return out;
}

export default function WCC360LeadCta() {
  const [parentName, setParentName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [childName, setChildName] = useState("");
  const [grade, setGrade] = useState("");
  const [school, setSchool] = useState("");
  const [city, setCity] = useState("");
  const [interests, setInterests] = useState<string[]>(["WCC360"]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const toggleInterest = (i: string, checked: boolean) => {
    setInterests((prev) => (checked ? [...new Set([...prev, i])] : prev.filter((x) => x !== i)));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    const p = parentName.trim();
    const em = email.trim().toLowerCase();
    const ph = phone.trim();
    const c = childName.trim();

    if (!p) return toast.error("Please enter the parent's name");
    if (!EMAIL_RE.test(em)) return toast.error("Please enter a valid email");
    if (!PHONE_RE.test(ph)) return toast.error("Please enter a valid mobile number");
    if (!c) return toast.error("Please enter the child's name");
    if (!GRADES.includes(grade)) return toast.error("Please select the child's grade");

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("wcc360-lead-submit", {
        body: {
          parent_name: p, email: em, phone: ph, child_name: c, grade,
          school: school.trim() || null,
          city: city.trim() || null,
          interests, message: message.trim() || null,
          utm: readUtm(),
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 400) : null,
        },
      });
      if (error) throw error;
      // Remember them so the guide page never asks for the same details again.
      saveParentLead({ name: p, email: em, mobile: ph || null, childAge: null });
      track("wcc360_lead_submitted", {
        surface: "homepage_cta",
        props: { is_repeat: !!(data as { is_repeat?: boolean })?.is_repeat, interests },
      });
      setDone(true);
    } catch (err) {
      console.error("wcc360 lead submit failed", err);
      toast.error("We couldn't submit your enquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="download-parent-guide" className="relative py-24 border-t border-border/50 overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0 -z-10 opacity-60"
        style={{ background: "radial-gradient(60% 50% at 50% 0%, hsl(var(--primary)/0.12), transparent 60%)" }}
      />

      <div className="container mx-auto px-6">
        <ScrollReveal>
          <div className="mx-auto max-w-3xl text-center mb-10">
            <Badge className="mb-4 rounded-full bg-primary/15 text-primary border-primary/30 gap-1.5 px-3 py-1.5">
              <Sparkles className="h-3.5 w-3.5" /> Career Clarity 360™
            </Badge>
            <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight text-foreground">
              Stop guessing your child's future.<br />
              <span className="gradient-text">Help them discover who they're becoming.&nbsp;<br />Help your child discover<br />the right career<br />before choosing subjects.</span>
            </h2>
            <p className="mt-5 text-muted-foreground text-base md:text-lg max-w-2xl mx-auto">
              The world of work is changing faster than ever. Give your child a personalised
              career intelligence journey that grows with them — not another one-time career test.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal>
          <div className="parent-notebook relative mx-auto max-w-3xl rounded-3xl border border-border bg-background pl-9 pr-5 py-8 sm:pl-14 sm:pr-8 md:pl-16 md:pr-10 md:py-10 text-foreground shadow-2xl">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="text-center py-8"
                >
                  <motion.div
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 240, damping: 18 }}
                    className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 ring-8 ring-primary/5"
                  >
                    <CheckCircle2 className="h-10 w-10 text-primary" aria-hidden />
                  </motion.div>
                  <h3 className="mt-6 font-display text-3xl md:text-4xl font-bold text-foreground">Thank you</h3>
                  <p className="mt-3 text-muted-foreground">Your details are in — we'll email the parent whitepaper shortly.</p>
                  <p className="mt-1 text-muted-foreground">Prefer to read it now? Grab a copy below.</p>
                  <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Button asChild size="lg" className="rounded-full h-12 px-6">
                      <Link to="/parent-guide"><Download className="mr-1.5 h-4 w-4" /> Download the parent whitepaper</Link>
                    </Button>
                    <Button asChild variant="outline" size="lg" className="rounded-full h-12 px-6">
                      <Link to="/offer">Explore WCC360 <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <div className="text-center mb-6">
                    <h3 className="font-display text-2xl md:text-3xl font-bold text-foreground">
                      Get the free parent whitepaper
                    </h3>
                    <p className="mt-2 text-muted-foreground">
                      A <span className="font-semibold text-foreground">2-page guide</span> to the
                      Career Clarity 360™ journey — no calls, just clarity in your inbox.
                    </p>
                  </div>

                  <form onSubmit={submit} noValidate className="grid gap-4 sm:grid-cols-2" aria-label="WCC360 lead capture">
                    <Field label="Parent name *" htmlFor="w-parent">
                      <Input id="w-parent" required autoComplete="name" maxLength={120}
                        value={parentName} onChange={(e) => setParentName(e.target.value)} />
                    </Field>
                    <Field label="Email *" htmlFor="w-email">
                      <Input id="w-email" type="email" required autoComplete="email" maxLength={255}
                        value={email} onChange={(e) => setEmail(e.target.value)} />
                    </Field>
                    <Field label="Mobile number *" htmlFor="w-phone">
                      <Input id="w-phone" type="tel" required inputMode="tel" autoComplete="tel" maxLength={32}
                        placeholder="+91 98XXXXXX00"
                        value={phone} onChange={(e) => setPhone(e.target.value)} />
                    </Field>
                    <Field label="Child's name *" htmlFor="w-child">
                      <Input id="w-child" required maxLength={120}
                        value={childName} onChange={(e) => setChildName(e.target.value)} />
                    </Field>
                    <Field label="Child's grade *" htmlFor="w-grade">
                      <Select value={grade} onValueChange={setGrade}>
                        <SelectTrigger id="w-grade" className="h-11">
                          <SelectValue placeholder="Select grade" />
                        </SelectTrigger>
                        <SelectContent className="auth-surface">
                          {GRADES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="School name" htmlFor="w-school">
                      <Input id="w-school" maxLength={160}
                        value={school} onChange={(e) => setSchool(e.target.value)} />
                    </Field>
                    <Field label="City" htmlFor="w-city">
                      <Input id="w-city" maxLength={80}
                        value={city} onChange={(e) => setCity(e.target.value)} />
                    </Field>

                    <div className="sm:col-span-2">
                      <Label className="mb-2 block">I'm interested in</Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {INTERESTS.map((i) => {
                          const id = `w-int-${i.replace(/\s+/g, "-")}`;
                          const checked = interests.includes(i);
                          return (
                            <label
                              key={i}
                              htmlFor={id}
                              className={`flex min-h-11 items-center gap-2 rounded-xl border px-3 py-2 cursor-pointer transition ${
                                checked ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                              }`}
                            >
                              <Checkbox id={id} checked={checked}
                                onCheckedChange={(v) => toggleInterest(i, !!v)} />
                              <span className="text-sm text-foreground">{i}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <Field label="Message (optional)" htmlFor="w-msg" className="sm:col-span-2">
                      <Textarea id="w-msg" rows={3} maxLength={2000}
                        placeholder="Anything you'd like us to know…"
                        value={message} onChange={(e) => setMessage(e.target.value)} />
                    </Field>

                    <div className="sm:col-span-2 flex flex-col items-center gap-3 mt-2">
                      <Button
                        type="submit" size="lg" disabled={loading}
                        className="h-12 px-8 rounded-full font-semibold hover-glow w-full sm:w-auto"
                      >
                        {loading ? (
                          <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</>
                        ) : (
                          <><FileText className="mr-2 h-4 w-4" /> Send me the parent whitepaper</>
                        )}
                      </Button>
                      <p className="text-xs text-muted-foreground text-center">
                        No spam. Instant download link plus one follow-up email.
                      </p>
                    </div>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

function Field({
  label, htmlFor, children, className,
}: { label: string; htmlFor: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <Label htmlFor={htmlFor} className="mb-1.5 block text-sm">{label}</Label>
      {children}
    </div>
  );
}
